import StyledButton from '@/components/common/styledButton';
import type {
  InvestorInviteSetting,
  BulkInviteRequest,
} from '@/store/clientsApi';
import {
  useBulkInviteInvestorsMutation,
  useGetInvestorInviteSettingQuery,
  // TODO(NU-940): Preview temporarily disabled; re-add when service is available
  // useLazyGetPreviewHtmlQuery,
  useInviteInvestorMutation,
} from '@/store/clientsApi';
import type { WARootState } from '@/store/store';
import { useGetAdvisorInvestorSetupQuery } from '@/store/usersApi';
import { DEBOUNCE_MS } from '@/constants/timing';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { deriveAdvisorGroupId, deriveAdvisorId } from './utils/advisorContext';
import {
  buildBulkInviteBody,
  buildSingleInviteBody,
} from './utils/invitePayloads';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { useSelector } from 'react-redux';
import EditInvitationTemplateModal from './EditInvitationTemplateModal';

type ClientInviteModalProps = {
  open: boolean;
  onClose: () => void;
  selectedCount: number;
  selectedInvestorIds: number[];
  selectedUserIds: number[];
  // When provided, avoids fetching invite settings here (parity with Clients.tsx preload)
  preloadedInviteSettings?: InvestorInviteSetting;
};

const ClientInviteModal = ({
  open,
  onClose,
  selectedCount,
  selectedInvestorIds,
  selectedUserIds,
  preloadedInviteSettings,
}: ClientInviteModalProps) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const appUserInfo = useSelector((s: WARootState) => s.appView?.UserInfo);
  const currentRole = useSelector((s: WARootState) => s.auth.currentRole);
  const { data: fetchedInviteSettings } = useGetInvestorInviteSettingQuery();
  const firstInvestorId = selectedInvestorIds?.[0];
  const firstUserId = selectedUserIds?.[0];
  const isSingleInvite = (selectedUserIds?.length || 0) === 1;
  const { data: perUserInviteSettings } = useGetInvestorInviteSettingQuery(
    { userId: firstUserId! },
    { skip: !open || !isSingleInvite || !firstUserId },
  );
  const effectiveInviteSettings: InvestorInviteSetting | undefined =
    preloadedInviteSettings ?? perUserInviteSettings ?? fetchedInviteSettings;
  const { data: firstInvestorSetup } = useGetAdvisorInvestorSetupQuery(
    firstUserId!,
    { skip: !open || !firstUserId },
  );

  // TODO(NU-940): Preview temporarily disabled; re-enable when preview service is available
  // const [triggerPreview] = useLazyGetPreviewHtmlQuery();
  // const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const [ccAdvisor, setCcAdvisor] = useState<boolean>(true);
  const [salutation, setSalutation] = useState<'first' | 'firstLast'>('first');
  const [additionalMessage, setAdditionalMessage] = useState<string>('');
  const [additionalEmails, setAdditionalEmails] = useState<string>('');
  const [signature, setSignature] = useState<string>(
    t('bulkInvite.defaultSignature', 'Best,'),
  );
  const [sender, setSender] = useState<string>('advisor');
  const [customSenderName, setCustomSenderName] = useState<string>('');

  // TODO(NU-940): Preview loading/error state (disabled for beta/UAT)
  // const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
  // const [previewError, setPreviewError] = useState<boolean>(false);
  // const [retryCount, setRetryCount] = useState<number>(0);

  // TODO(NU-940): Preview disabled for beta/UAT; remove when enabling preview
  // const invitePreviewEnabled = import.meta.env.MODE === "production";

  // TODO(NU-940): Ensure error/loading overlays never show in beta (preview disabled)
  // useEffect(() => {
  //   if (!invitePreviewEnabled) {
  //     setIsPreviewLoading(false);
  //     setPreviewError(false);
  //   }
  // }, [invitePreviewEnabled]);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  } | null>(null);

  const title = isSingleInvite
    ? firstInvestorSetup?.Name || t('bulkInvite.title', 'Bulk Invite')
    : t('bulkInvite.title', 'Bulk Invite');

  // Edit Invitation Template modal state
  const [isEditInviteOpen, setIsEditInviteOpen] = useState<boolean>(false);

  // Derive advisor and group ids to align single-invite payload with legacy
  const advisorIdFromContext: number | null = useMemo(() => {
    return deriveAdvisorId(appUserInfo, firstInvestorSetup?.PrimaryAdvisorId);
  }, [appUserInfo, firstInvestorSetup]);

  const advisorGroupIdFromContext: number | null = useMemo(() => {
    return deriveAdvisorGroupId(appUserInfo);
  }, [appUserInfo]);

  useEffect(() => {
    if (!open) return;
    const useLastName = effectiveInviteSettings?.UseLastName
      ? 'firstLast'
      : 'first';
    setSalutation(useLastName);
    setAdditionalMessage(effectiveInviteSettings?.AdditionalMessage || '');
    setSignature(
      effectiveInviteSettings?.CustomSignature ||
        t('bulkInvite.defaultSignature', 'Best,'),
    );
    if (effectiveInviteSettings) {
      const defaultInvitor = effectiveInviteSettings.UseGroupName
        ? effectiveInviteSettings.AdvisorGroupName
        : effectiveInviteSettings.AdvisorName;
      setSender(defaultInvitor || 'advisor');
    }
  }, [open, effectiveInviteSettings, t]);

  // Do not persist CCAdvisor default here (legacy bulk modal does not POST user settings on open)

  const canEditInvitation = useMemo(() => {
    const restrictions = appUserInfo?.Restrictions || [];
    return !restrictions.some(
      (r) =>
        (r.Type || '') === 'Code' && (r.Value || '') === 'CanEditInvitation',
    );
  }, [appUserInfo]);

  const senderOptions = useMemo(() => {
    const opts: string[] = [];
    if (effectiveInviteSettings?.AdvisorGroupName)
      opts.push(effectiveInviteSettings.AdvisorGroupName);
    if (
      effectiveInviteSettings?.AdvisorName &&
      effectiveInviteSettings.AdvisorName !==
        effectiveInviteSettings.AdvisorGroupName
    )
      opts.push(effectiveInviteSettings.AdvisorName);
    if (effectiveInviteSettings?.LoggedInUserName)
      opts.push(effectiveInviteSettings.LoggedInUserName);
    opts.push('Custom');
    return opts;
  }, [effectiveInviteSettings]);

  const useLastName = salutation === 'firstLast';

  // Debounce preview-driving inputs to avoid firing preview too often while typing
  const debouncedAdditionalMessage = useDebouncedValue(
    additionalMessage,
    DEBOUNCE_MS.DEFAULT,
  );
  const debouncedSignature = useDebouncedValue(signature, DEBOUNCE_MS.DEFAULT);
  const debouncedSender = useDebouncedValue(sender, DEBOUNCE_MS.FAST);
  const debouncedCustomSenderName = useDebouncedValue(
    customSenderName,
    DEBOUNCE_MS.DEFAULT,
  );

  // TODO(NU-940): Preview effect disabled until preview service is available in UAT/beta
  // useEffect(() => {
  //   if (!invitePreviewEnabled) return;
  //   if (!open || !iframeRef.current) return;
  //   if (!firstInvestorId || !effectiveInviteSettings) return;
  //   setIsPreviewLoading(true);
  //   setPreviewError(false);
  //   const investorName = firstInvestorSetup?.Name || "";
  //   let firstName = investorName.split(" ")[0] || "";
  //   let lastName = investorName.split(" ").slice(1).join(" ") || "";
  //   const salutationText = `Hi ${firstName}${useLastName && lastName ? ` ${lastName}` : ""},`;
  //   const invitorNameFromSettings = effectiveInviteSettings?.UseGroupName
  //     ? effectiveInviteSettings.AdvisorGroupName || ""
  //     : effectiveInviteSettings?.AdvisorName || "";
  //   const invitorName = debouncedSender === "Custom"
  //     ? (debouncedCustomSenderName || "Custom")
  //     : (debouncedSender || invitorNameFromSettings);
  //   triggerPreview({
  //     investorId: firstInvestorId,
  //     investorName,
  //     options: { /* omitted for brevity */ },
  //   })
  //     .unwrap()
  //     .then(() => setIsPreviewLoading(false))
  //     .catch(() => {
  //       setPreviewError(true);
  //       setIsPreviewLoading(false);
  //     });
  // }, [/* dependencies */]);

  const ccLabel = useMemo(() => {
    const isImpersonation = !!appUserInfo?.IsImpersonation;
    const role = (currentRole || '').toLowerCase();
    if (isImpersonation || role === 'firm')
      return t('bulkInvite.ccAdvisor', 'CC Advisor');
    return t('bulkInvite.ccMyself', 'CC Myself');
  }, [appUserInfo, currentRole, t]);

  const [sendInvites, { isLoading: isSendingBulk }] =
    useBulkInviteInvestorsMutation();
  const [inviteInvestor, { isLoading: isSendingSingle }] =
    useInviteInvestorMutation();
  const isSending = isSendingBulk || isSendingSingle;

  const handleSend = async () => {
    const invitorName =
      sender === 'Custom' ? customSenderName || 'Custom' : sender;
    try {
      if (isSingleInvite && firstUserId && firstInvestorSetup) {
        const firstName = firstInvestorSetup.FirstName || '';
        const lastName = firstInvestorSetup.LastName || '';
        const salutationText = `Hi ${firstName}${useLastName && lastName ? ` ${lastName}` : ''},`;
        const body = buildSingleInviteBody({
          setup: firstInvestorSetup,
          ccAdvisor,
          useLastName,
          signature,
          invitorName,
          sender,
          customSenderName,
          additionalMessage,
          additionalEmails,
          effectiveInviteSettings,
          advisorIdFromContext,
          advisorGroupIdFromContext,
          salutationText,
        });
        await inviteInvestor({ userId: firstUserId, body }).unwrap();
      } else {
        const userIds = (selectedUserIds || [])
          .map((x) => Number(x))
          .filter((x) => !Number.isNaN(x));
        const body: BulkInviteRequest = buildBulkInviteBody({
          userIds,
          useLastName,
          signature,
          invitorName,
          sender,
          customSenderName,
          ccAdvisor,
          additionalMessage,
          effectiveInviteSettings,
        });
        await sendInvites(body).unwrap();
      }
      setSnackbar({
        open: true,
        message: t('bulkInvite.sentSuccess', 'Invites sent successfully!'),
        severity: 'success',
      });
    } catch {
      // swallow errors silently
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(null);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth='lg'
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography
            variant='h5'
            sx={{ fontSize: '1.25rem' }}
          >
            {title}
          </Typography>
          <Box>
            <IconButton
              onClick={onClose}
              aria-label={t('close', 'Close')}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Grid
            container
            spacing={3}
          >
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack gap={2}>
                <Box sx={{ mt: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={ccAdvisor}
                        onChange={(e) => setCcAdvisor(e.target.checked)}
                      />
                    }
                    label={ccLabel}
                  />
                </Box>

                {isSingleInvite && (
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant='subtitle1'
                      sx={{ mb: 1 }}
                    >
                      {t(
                        'bulkInvite.ccAdditionalPeople',
                        'CC Additional People',
                      )}
                    </Typography>
                    <TextField
                      value={additionalEmails}
                      onChange={(e) => setAdditionalEmails(e.target.value)}
                      placeholder={t(
                        'bulkInvite.additionalEmailsPlaceholder',
                        'email1@example.com; email2@example.com',
                      )}
                      helperText={t(
                        'bulkInvite.additionalEmailsHelp',
                        'emails separate by semicolon(;)',
                      )}
                      fullWidth
                    />
                  </Box>
                )}

                <Box>
                  <Typography
                    variant='h6'
                    sx={{ mb: 1.5 }}
                  >
                    {t('bulkInvite.customizeInvite', 'Customize Invite')}
                  </Typography>
                  {canEditInvitation && (
                    <Box sx={{ mb: 1 }}>
                      <Link
                        component='button'
                        type='button'
                        underline='hover'
                        onClick={() => setIsEditInviteOpen(true)}
                      >
                        {t(
                          'bulkInvite.editInvitationTemplate',
                          'Edit Invitation Template',
                        )}
                      </Link>
                    </Box>
                  )}

                  {canEditInvitation && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant='subtitle1'
                        sx={{ mb: 1 }}
                      >
                        {t('bulkInvite.salutation', 'Salutation')}{' '}
                        <Typography
                          component='span'
                          color='error'
                        >
                          *
                        </Typography>
                      </Typography>
                      <RadioGroup
                        value={salutation}
                        onChange={(e) =>
                          setSalutation(e.target.value as 'first' | 'firstLast')
                        }
                      >
                        <FormControlLabel
                          value='first'
                          control={<Radio />}
                          label={t('bulkInvite.useFirstName', 'Use First Name')}
                        />
                        <FormControlLabel
                          value='firstLast'
                          control={<Radio />}
                          label={t(
                            'bulkInvite.useFirstAndLastName',
                            'Use First and Last Name',
                          )}
                        />
                      </RadioGroup>
                    </Box>
                  )}

                  {canEditInvitation && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant='subtitle1'
                        sx={{ mb: 1 }}
                      >
                        {t(
                          'bulkInvite.addPersonalMessage',
                          'Add a Personal Message',
                        )}{' '}
                        <Typography
                          component='span'
                          color='text.secondary'
                        >
                          {t('optional', '(Optional)')}
                        </Typography>
                      </Typography>
                      <TextField
                        value={additionalMessage}
                        onChange={(e) => setAdditionalMessage(e.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                      />
                    </Box>
                  )}

                  {canEditInvitation && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant='subtitle1'
                        sx={{ mb: 1 }}
                      >
                        {t('bulkInvite.signature', 'Signature')}{' '}
                        <Typography
                          component='span'
                          color='error'
                        >
                          *
                        </Typography>
                      </Typography>
                      <TextField
                        required
                        value={signature}
                        onChange={(e) => setSignature(e.target.value)}
                        fullWidth
                      />
                    </Box>
                  )}

                  {canEditInvitation && (
                    <Box>
                      <Typography
                        variant='subtitle1'
                        sx={{ mb: 0.5 }}
                      >
                        {t('bulkInvite.sender', 'Sender')}{' '}
                        <Typography
                          component='span'
                          color='error'
                        >
                          *
                        </Typography>
                      </Typography>
                      <Typography
                        variant='subtitle2'
                        color='text.secondary'
                        sx={{ display: 'block', mb: 1.5 }}
                      >
                        {t(
                          'bulkInvite.senderHint',
                          'The email body says who sent the invitation twice, choose who you want to be the invitor.',
                        )}
                      </Typography>
                      <RadioGroup
                        value={sender}
                        onChange={(e) => setSender(e.target.value)}
                      >
                        {senderOptions.map((opt) => (
                          <FormControlLabel
                            key={opt}
                            value={opt}
                            control={<Radio />}
                            label={t(
                              'bulkInvite.sendFromName',
                              'Send email from {{name}}',
                              { name: opt },
                            )}
                          />
                        ))}
                        {sender === 'Custom' && (
                          <Box sx={{ mt: 1 }}>
                            <TextField
                              placeholder={t(
                                'bulkInvite.otherPlaceholder',
                                'other',
                              )}
                              size='small'
                              value={customSenderName}
                              onChange={(e) =>
                                setCustomSenderName(e.target.value)
                              }
                              fullWidth
                            />
                          </Box>
                        )}
                      </RadioGroup>
                    </Box>
                  )}
                </Box>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.grey[50],
                  borderRadius: 1,
                  maxHeight: 600,
                  overflow: 'auto',
                }}
              >
                <Box sx={{ position: 'relative', pt: '56.25%' }}>
                  <Box sx={{ position: 'absolute', inset: 0 }}>
                    {/* TODO(NU-940): Invitation preview temporarily disabled — show placeholder until service is available in UAT/beta */}
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2,
                      }}
                    >
                      <Box sx={{ maxWidth: 520, textAlign: 'center' }}>
                        <Typography
                          variant='h6'
                          sx={{ mb: 1 }}
                        >
                          {t(
                            'bulkInvite.previewUnavailableTitle',
                            'Invitation preview unavailable in beta',
                          )}
                        </Typography>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                        >
                          {t(
                            'bulkInvite.previewUnavailableDescription',
                            'The email invitation preview is disabled in this environment. It will be available in production.',
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: 'flex-end',
            borderTop: 1,
            borderColor: 'divider',
            px: 3,
            py: 2,
          }}
        >
          <Button
            variant='outlined'
            size='large'
            onClick={onClose}
            disabled={isSending}
          >
            {t('cancel', 'Cancel')}
          </Button>
          <StyledButton
            variant='contained'
            size='large'
            onClick={handleSend}
            disabled={isSending}
          >
            {t('bulkInvite.sendInvites', {
              defaultValue: 'Send {{count}} Invites',
              count: selectedCount,
            })}
          </StyledButton>
        </DialogActions>

        {/* Edit Invitation Template Modal */}
        {canEditInvitation && (
          <EditInvitationTemplateModal
            open={isEditInviteOpen}
            onClose={() => setIsEditInviteOpen(false)}
            isFullscreen={isMobile}
            investorId={firstInvestorId}
            investorName={firstInvestorSetup?.Name || ''}
            useLastName={useLastName}
            additionalMessage={additionalMessage}
            signature={signature}
            invitorName={
              sender === 'Custom' ? customSenderName || 'Custom' : sender
            }
            inviteSettings={effectiveInviteSettings}
          />
        )}
      </Dialog>
      {snackbar ? (
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      ) : null}
    </>
  );
};

export default ClientInviteModal;
